import axios from 'axios';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAppDispatch } from '@/core/global-store';
import { setCredentials } from '@/core/global-store/features/auth/authSlice';
import { endpoints } from '@/lib/axios/axios-global';
import { setSession } from '@/features/auth/guard/utils';
import {
    getImpersonationMeta,
    isImpersonating,
    restoreImpersonatorSession,
} from '@/lib/auth/impersonation';
import SubscriptionButton from '@/features/subscriptions/components/SubscriptionButton';

export function ImpersonationBanner() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const active = isImpersonating();
    const meta = getImpersonationMeta();

    const handleExit = useCallback(async () => {
        const { token, me, permissions } = restoreImpersonatorSession();
        if (!token) {
            toast.error(t('subscription_impersonation_restore_failed'));
            return;
        }

        setSession(token);
        if (permissions) {
            localStorage.setItem('permissions', JSON.stringify(permissions));
        }

        dispatch(setCredentials({ token, me: me as Parameters<typeof setCredentials>[0]['me'] }));

        try {
            const meRes = await axios.get(endpoints.auth.me);
            dispatch(setCredentials({ token, me: meRes.data }));
        } catch {
            /* keep restored session */
        }

        toast.success(t('subscription_impersonation_restored'));
        navigate('/subscriptions', { replace: true });
        window.location.reload();
    }, [dispatch, navigate, t]);

    if (!active) return null;

    const userLabel = meta?.target_name ?? meta?.target_email ?? `#${meta?.target_user_id ?? ''}`;

    return (
        <div className="border-b border-primery-blue-200 bg-primery-blue-50 px-4 py-2 text-sm text-primery-blue-900">
            <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2">
                <span>
                    {t('subscription_impersonation_active', { name: userLabel })}
                    {meta?.impersonator?.name ? (
                        <span className="text-primery-blue-600">
                            {' '}
                            ({t('subscription_impersonation_from', { name: meta.impersonator.name })})
                        </span>
                    ) : null}
                </span>
                <SubscriptionButton type="button" variant="secondary" onClick={() => void handleExit()}>
                    {t('subscription_impersonation_exit')}
                </SubscriptionButton>
            </div>
        </div>
    );
}
